import LoginUtils from "@/utils/login/LoginUtils";
import MenuTargetType from "@/utils/constants/MenuTargetType";
import ClientCache from "@/utils/login/ClientCache";
import ObjectChecker from "@/utils/common/ObjectChecker";
import Translator from "@/utils/common/Translator";
import BSCEntities from "@/utils/constants/BSCEntities";
import DomainBaseFieldIDs from "@/utils/constants/DomainBaseFieldIDs";
import {UIMenuGroup, UIMenuItem, UIMenuSubGroup} from "@/utils/UIConstants/UIMenuGroup";
import {LocationQueryRaw, RouteLocationRaw, RouteRecordRaw} from "vue-router";
import RouteNames from "@/router/RouteNames.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import CapabilityType from "@/utils/constants/CapabilityType.ts";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import ConstantViewNames from "@/utils/constants/ConstantViewNames.ts";
import {QBtnProps} from "quasar";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import EditPlace from "@/utils/presenters/EditPlace.ts";
import router from "@/router";
import RouteSecurity from "@/router/RouteSecurity.ts";
import DashboardUtils from "@/utils/common/DashboardUtils.ts";
import DTOMenuTarget = namaTypes.DTOMenuTarget;
import EntityReferenceData = namaTypes.EntityReferenceData;
import DTOMenuItem = namaTypes.DTOMenuItem;
import DTOMenuDefinition = namaTypes.DTOMenuDefinition;
import DTOUserFavourites = namaTypes.DTOUserFavourites;
import DTOMenuGroup = namaTypes.DTOMenuGroup;
import DTOUserFavouritesGroupsLine = namaTypes.DTOUserFavouritesGroupsLine;
import DTOUserFavouritesLine = namaTypes.DTOUserFavouritesLine;
import DTOCardMenuLine = namaTypes.DTOCardMenuLine;

export default class SideBarUtils {
  private static menuDetails: UIMenuGroup[] = [];

  static isSidebarFixed(): boolean {
    return useNamaFlagsStore().sidebarFixed || !!ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.fixMenuAsSideBar;
  }

  private static appendUtilsToMenuIfNeeded(menu: DTOMenuDefinition) {
    if (LoginUtils.loginInfo?.userInfo?.loginId !== 'admin' && !LoginUtils.loginInfo?.userInfo?.settings?.treatAsAdmin)
      return;
    let settings = menu.groups?.find(g => g.code === "SettingsSub");
    if (!settings)
      return;
    let utils = menu.items?.find(it => it.code === "utils-menu");
    if (utils)
      return;
    menu.items!.push({
      groupCode: settings.code,
      id: 'bbaa0001-94d5-9e11-d302-2500ff0340c5',
      code: "utils-menu",
      name1: "Utilities",
      name2: "Utilities",
      iconCode: "admin_panel_settings",
      target: {type: "InternalLink", link: "utils"}
    })
  }

  static findMenuItem(menu?: string | null) {
    if (menu) {
      for (let mg of this.fetchMenuDetails()) {
        for (let sg of mg.subGroups) {
          for (let item of sg.subItems) {
            if (item.code === menu || item.id === menu) {
              return {mg, sg, item};
            }
          }
        }
      }
    }
  }

  static fetchMenuDetails(): UIMenuGroup[] {
    if (this.menuDetails.length)
      return this.menuDetails;
    let menu = LoginUtils.loginInfo?.menu;
    if (!menu)
      return [];
    this.appendUtilsToMenuIfNeeded(menu)
    const menuItems = menu.items ?? [];
    for (let root of menu.groups ?? []) {
      if (!root.rootGroup)
        continue;
      if (!this.shouldGroupAdded(root))
        continue;
      const mySubGroups = menu.groups!.filter(l => !l.rootGroup && root.code === l.parentCode);
      this.expandGroupsIfAutoDashboards(mySubGroups, menu);
      const menuGroup: UIMenuGroup = new UIMenuGroup();
      for (const leafGroup of mySubGroups) {
        if (root.code !== leafGroup.parentCode)
          continue;
        if (!this.shouldGroupAdded(leafGroup))
          continue;
        const menuSubGroup: UIMenuSubGroup = new UIMenuSubGroup();
        menuSubGroup.subGroupData = leafGroup;
        for (let menuItem of menuItems) {
          if (menuItem.groupCode !== leafGroup.code)
            continue;
          this.addMenuItem(menu, menuItem, menuSubGroup);
        }
        if (!menuSubGroup.subItems || !menuSubGroup.subItems.length)
          continue;
        menuSubGroup.subGroupData = leafGroup;
        menuGroup.subGroups.push(menuSubGroup);
      }
      if (!menuGroup.subGroups || !menuGroup.subGroups.length)
        continue;
      menuGroup.groupData = root;
      this.menuDetails.push(menuGroup);
    }
    return this.menuDetails;
  }

  static shouldGroupAdded(group: namaTypes.DTOMenuGroup) {
    if (!ClientCache.isModuleLoaded(group.relatedToModule) && !ClientCache.isModuleNotEmptyAndLoaded(group.relatedToSubModule))
      return false;
    if (!SecurityUtils.userCanFeature(group.relatedToFeature))
      return false;
    return true;
  }

  private static addMenuItem(menu: DTOMenuDefinition, childItem: namaTypes.DTOMenuItem, childGroup: UIMenuSubGroup) {
    if (!childItem || !childItem.target)
      return;
    if (ObjectChecker.areEqual(MenuTargetType.AllReports, childItem.target.type)) {
      this.addAllReports(childGroup);
    } else if (ObjectChecker.areEqual(MenuTargetType.UserFavourites, childItem.target.type)) {
      this.addUserFavourites(menu, childGroup);
    } else if (ObjectChecker.areEqual(MenuTargetType.AutoDashBoards, childItem.target.type)) {
      this.addAutoDashBoardsGroups(childGroup, childItem);
    } else if (ObjectChecker.areEqual(MenuTargetType.DashboardsGroup, childItem.target.type)) {
      this.addAllDashboardsOf(childGroup, childItem, ClientCache.getDashboardsOfGroup(childItem?.target?.targetRecord));
    } else {
      this.addItemToGroup(childGroup, childItem);
    }
  }

  static addUserFavourites(menu: DTOMenuDefinition, childGroup: UIMenuSubGroup) {
    let parentGroupCode: string | undefined = childGroup.subGroupData.parentCode;
    let favourites: DTOUserFavourites | undefined = LoginUtils.loginInfo?.favourites;
    if (!favourites || !favourites.favouriteItems || !favourites.groupsLines)
      return;
    if (!menu.groups)
      menu.groups = [];
    for (let i = 0; i < favourites.groupsLines.length; i++) {
      childGroup.subGroupData = this.toMenuGroup(favourites.groupsLines[i])
      childGroup.subGroupData.parentCode = parentGroupCode;
      let items: DTOUserFavouritesLine[] = this.itemsOf(favourites, childGroup.subGroupData.code);
      if (!items)
        continue;
      menu.groups.push(childGroup.subGroupData);
      for (let item of items)
        this.addItemToGroup(childGroup, item);
    }
  }

  static toMenuGroup(favouriteLine: DTOUserFavouritesGroupsLine): DTOMenuGroup {
    return {
      code: favouriteLine.code,
      titleId: favouriteLine.code,
      name1: favouriteLine.name1,
      name2: favouriteLine.name2
    };
  }

  static itemsOf(userFavourites: DTOUserFavourites, groupCode: string | undefined): DTOUserFavouritesLine[] {
    if (!userFavourites.favouriteItems)
      return []
    return userFavourites.favouriteItems.filter(f => groupCode === f.parentGroupCode)
      .filter(l => !!l.targetFile).map(f => this.toMenuItem(f));
  }

  static toMenuItem(userFavourites: DTOUserFavouritesLine) {
    let item: DTOMenuItem = {};
    item.target = {
      targetRecord: userFavourites.targetFile,
      type: MenuTargetType.Record,
      viewName: userFavourites.viewName || ""
    };
    item.groupCode = userFavourites.parentGroupCode;
    let entityType: string | undefined = userFavourites.targetFile?.entityType;
    item.code = userFavourites.parentGroupCode + "-" + ObjectChecker.getFirstNotEmptyObj(userFavourites.targetFile?.code, userFavourites.targetFile?.altCode,
      entityType)
    if (userFavourites.targetFile && !userFavourites.targetFile.code && !userFavourites.targetFile.id && !userFavourites.targetFile.altCode) {
      item.target.targetEntity = userFavourites.targetFile.entityType;
      item.target.type = MenuTargetType.NewRecord;
    } else if (ObjectChecker.areEqual(entityType, BSCEntities.DashBoard)) {
      item.target.type = MenuTargetType.Dashboard;
    } else if (ObjectChecker.areEqual(entityType, BSCEntities.ReportDefinition)) {
      item.target.type = MenuTargetType.Report;
    } else if (ObjectChecker.areEqual(entityType, BSCEntities.ReportGroup)) {
      item.target.type = MenuTargetType.ReportsGroup;
    } else if (ObjectChecker.areEqual(entityType, BSCEntities.DashBoardGroup)) {
      item.target.type = MenuTargetType.DashboardsGroup;
    }
    if (ObjectChecker.areEqual(userFavourites.elementType, "CreateNew")) {
      item.target.type = MenuTargetType.NewRecord;
    } else if (ObjectChecker.areEqual(userFavourites.elementType, "ListView")) {
      item.target.type = MenuTargetType.EntityList;
    }
    return item;
  }

  static addAllReports(reportsSubGroup: UIMenuSubGroup) {
    let rootReportGroups = ClientCache.getRootReportGroups();
    if (!rootReportGroups)
      return;
    for (let reportGroup of rootReportGroups) {
      let item = this.convertReportGroupToMenuItem(reportGroup);
      this.addItemToGroup(reportsSubGroup, item);
    }
  }

  public static convertReportGroupToMenuItem(rootReport: namaTypes.EntityReferenceData) {
    const item: namaTypes.DTOMenuItem = {
      code: rootReport.code,
      id: rootReport.id,
      name1: rootReport.name1,
      name2: rootReport.name2,
      target: {
        targetRecord: rootReport,
        type: MenuTargetType.ReportsGroup,
        targetEntity: BSCEntities.ReportDefinition
      }
    };
    return item;
  }

  private static addItemToGroup(group: UIMenuSubGroup, item: namaTypes.DTOMenuItem) {
    if (!group.subItems)
      group.subItems = [];
    if (this.itemCanBeShown(group, item)) {
      let xItem: UIMenuItem = item as UIMenuItem;
      xItem.searchValue = this.calcMenuItemSearchStr(xItem);
      group.subItems.push(xItem);
    }
  }

  private static addAutoDashBoardsGroups(dashboardGroup: UIMenuSubGroup, menuItem: DTOMenuItem) {
    let autoDashBoardGroups: EntityReferenceData[] = ClientCache.getAutoDashBoardGroups();
    if (!autoDashBoardGroups)
      return;
    for (let group of autoDashBoardGroups) {
      this.addAllDashboardsOf(dashboardGroup, menuItem, ClientCache.getDashboardsOfGroup(group));
    }
  }

  private static addAllDashboardsOf(dashboardGroup: UIMenuSubGroup, menuItem: DTOMenuItem, dashboards: EntityReferenceData[]) {
    if (!dashboards || !dashboards.length)
      return;
    for (let dashboard of dashboards) {
      let item = this.convertDashboardToMenuItem(dashboard, menuItem);
      this.addItemToGroup(dashboardGroup, item);
    }
  }

  public static convertDashboardToMenuItem(dashboard: namaTypes.EntityReferenceData, menuItem?: namaTypes.DTOMenuItem) {
    let dashBoardId = (dashboard.id ?? '').replaceAll("-", "");
    let item: DTOMenuItem = {
      id: (menuItem?.code || "") + dashBoardId,
      titleId: dashboard ? Translator.calcByLanguage(dashboard.name1 ?? '', dashboard.name2 ?? '') : "",
      target: {
        type: MenuTargetType.Dashboard,
        targetRecord: dashboard,
        targetEntity: BSCEntities.DashBoardGroup
      }
    };
    return item;
  }

  private static expandGroupsIfAutoDashboards(groups: namaTypes.DTOMenuGroup[], menu: namaTypes.DTOMenuDefinition) {
    let autoDashBoardGroups: any[] = ClientCache.getAutoDashBoardGroups();
    if (ObjectChecker.isEmptyOrNull(autoDashBoardGroups))
      return;
    for (let i = groups.length - 1; i >= 0; i--) {
      let group = groups[i];
      let items = menu.items?.filter(item => item.groupCode === group.code);
      if (!items || items.length != 1)
        continue;
      let autoDashboard = items.find(it => ClientCache.fetchTargetType(it) === MenuTargetType.AutoDashBoards);
      if (!autoDashboard)
        continue;
      menu.items?.splice(menu.items.indexOf(autoDashboard), 1);
      groups.splice(groups.indexOf(group), 1);
      for (let ag of autoDashBoardGroups) {
        let g: namaTypes.DTOMenuGroup = {};
        g.code = group.code + ag.code;
        g.name1 = ag.name1;
        g.name2 = ag.name2;
        g.id = ag.id;
        g.rootGroup = false;
        g.parentCode = group.parentCode;
        groups.push(g);
        menu.groups?.push(g);
        let item: namaTypes.DTOMenuItem = {};
        item.id = ag.id;
        item.code = g.code;
        item.groupCode = g.code;
        item.target = {
          targetRecord: ag,
          type: MenuTargetType.DashboardsGroup
        };
        menu.items?.push(item);
      }
    }
  }

  static findParentGroup(code: string | undefined, groups: UIMenuGroup[]) {
    for (let i = 0; i < groups.length; i++)
      if (groups[i].groupData.code === code)
        return groups[i];
    return null;
  }

  static itemCanBeShown(group: UIMenuSubGroup, item: UIMenuItem) {
    if (this.itemPreventedBySecurityProfile(group, item))
      return false;
    if (!item.target)
      return true;
    if (ObjectChecker.isAnyEqualToFirst(item.target.type, MenuTargetType.UserFavourites, MenuTargetType.AllReports, MenuTargetType.DashboardsGroup))
      return true;
    this.createPlaceOrLinkForMenuItem(item);
    if (!item.place)
      return false;
    return this.isAllowedInCurrentSecurityProfile(item.place);
  }

  static isAllowedInCurrentSecurityProfile(place: RouteLocationRaw) {
    let query = place['query'] || {};
    if (!RouteSecurity.canEnter(router.resolve(place)))
      return false;
    let entity = query[PlaceTokens.PRM_ENTITY_TYPE];
    if (!entity)
      return true;
    if (!ClientCache.isEntityEnabled(entity))
      return false;
    let capabilities: string[] = [];
    const placeElement = place['name'];
    if (ObjectChecker.isAnyEqualToFirst(placeElement, RouteNames.LIST_VIEW_ROUTE, RouteNames.REPORT_LIST_ROUTE, RouteNames.WELCOME_ROUTE)) {
      capabilities.push(CapabilityType.ListView);
    } else if (placeElement === RouteNames.EDIT_VIEW_ROUTE) {
      capabilities.push(CapabilityType.RecordView);
    }
    for (let capability of capabilities) {
      if (!SecurityUtils.userCan(entity, capability))
        return false;
    }
    return true;
  }

  static createPlaceOrLinkForMenuItem(item: UIMenuItem, forCardMenu?: boolean): RouteLocationRaw | string | null | undefined {
    let target: DTOMenuTarget | undefined = item.target;
    if (!target) {
      if (item.place) {
        return item.place;
      } else {
        return null;
      }
    }
    switch (target.type) {
      case MenuTargetType.EntityList:
        item.place = this.createListRoute(item);
        this.updateCriteriaAndTemplateInPlace(item, forCardMenu);
        break;
      case MenuTargetType.NewRecord:
        item.place = this.createEditRoute(item, ViewMode.NEW);
        this.updateCriteriaAndTemplateInPlace(item, forCardMenu);
        break;
      case MenuTargetType.Report:
        item.place = this.createReportLauncherPlace(item);
        break;
      case MenuTargetType.ReportsGroup:
        item.place = this.createListReportRoute(item);
        break;
      case MenuTargetType.Record:
        item.place = this.createEditRoute(item, ViewMode.EDIT);
        this.updateCriteriaAndTemplateInPlace(item, forCardMenu);
        break;
      case MenuTargetType.InternalLink:
        let link = item.target?.link;
        if (link) {
          if (link.startsWith("#"))
            link = link.substring(1);
          if (link.startsWith("/"))
            link = link.substring(1);
          item.place = {path: link, query: {[PlaceTokens.PRM_MENU_HASH]: item.code || item.id}}
        }
        break;
      case MenuTargetType.Link:
        return item.target?.link
      case MenuTargetType.Dashboard:
        item.place = this.createWelcomeRoute(item);
        break;
    }
    if (item.place)
      return item.place;
  }

  private static updateCriteriaAndTemplateInPlace(item: UIMenuItem, forCardMenu?: boolean) {
    if (!item.place)
      return;
    if (!forCardMenu)
      return;
    let query = (item.place as any).query;
    if (!query)
      return;
    if (item.target?.criteria)
      query[PlaceTokens.PRM_EXTRA_CRITERIA] = item.target.criteria.id;
    if (item.target?.defaultsTemplate)
      query[PlaceTokens.PRM_EDIT_TEMPLATE_ID] = item.target.defaultsTemplate.id;
  }

  static createLinkPropsForMenuItem(item: DTOCardMenuLine, forCardMenu?: boolean): Pick<QBtnProps, "to" | "target" | "href" | "onClick"> | undefined {
    const placeOrLink = SideBarUtils.createPlaceOrLinkForMenuItem(item, forCardMenu);
    if (ObjectChecker.isEmptyOrNull(placeOrLink))
      return;
    let query: LocationQueryRaw | undefined | null;
    if (ObjectChecker.isString(placeOrLink)) {
      if (!DashboardUtils.canOpenQuickEditReferenceDialog(placeOrLink))
        return {href: placeOrLink, target: "_blank"};
      query = PlaceUtils.getQueryFromUrl(PlaceUtils.normalizedGUILink(placeOrLink))
    } else if (DashboardUtils.canOpenQuickEditReferenceDialog(placeOrLink.path) || (<RouteRecordRaw>placeOrLink).name == RouteNames.EDIT_VIEW_ROUTE)
      query = placeOrLink.query;
    if (query && query[PlaceTokens.PRM_ENTITY_TYPE] && query[PlaceTokens.PRM_ID]) {
      const editPlace = new EditPlace(query);
      return {
        onClick: (event: MouseEvent & KeyboardEvent) => {
          const editLink = editPlace.toLink();
          if (event.altKey) {
            router.push(editLink)
            return;
          }
          if (event.ctrlKey) {
            window.open(editLink, '_blank');
          } else
            DashboardUtils.openQuickEditReferenceDialog(editPlace);
        }
      }
    } else
      return {to: placeOrLink};
  }

  private static createEditRoute(item: UIMenuItem, viewMode: ViewMode): RouteLocationRaw {
    let target = item.target!;
    let query: LocationQueryRaw = {
      [PlaceTokens.PRM_MENU_HASH]: item.code,
      [PlaceTokens.PRM_ENTITY_TYPE]: target.targetEntity,
      [PlaceTokens.PRM_EDIT_VIEW_NAME]: target.viewName,
      [PlaceTokens.PRM_VIEW]: viewMode + '',
    }
    if (viewMode === ViewMode.EDIT && target.targetRecord && target.targetRecord.id) {
      query[PlaceTokens.PRM_MENU_HASH] = target.targetRecord.code;
      query[PlaceTokens.PRM_ID] = target.targetRecord.id;
      query[PlaceTokens.PRM_ENTITY_TYPE] = target.targetRecord.entityType;
    }
    return {
      name: RouteNames.EDIT_VIEW_ROUTE,
      query: query
    };
  }

  private static createListReportRoute(item: UIMenuItem): RouteLocationRaw {
    let targetRecord = item.target!.targetRecord!;
    let query: LocationQueryRaw = {
      [PlaceTokens.PRM_MENU_HASH]: item.id || targetRecord.id || item.code,
      [PlaceTokens.PRM_ENTITY_TYPE]: BSCEntities.ReportDefinition,
      [PlaceTokens.PRM_LIST_VIEW_NAME]: ConstantViewNames.REPORT_CATALOGUE_SCREEN_NAME,
      [PlaceTokens.PRM_ROOT]: targetRecord.id || item.code,
      [PlaceTokens.PRM_ROOT_TYPE]: targetRecord.entityType,
      [PlaceTokens.PRM_SHOW_TREE]: true + '',
    }
    return {
      name: RouteNames.REPORT_LIST_ROUTE,
      query: query
    };
  }

  private static createReportLauncherPlace(item: UIMenuItem): RouteLocationRaw {
    let targetId: string | undefined = item.target!.targetRecord!.id;
    let query: LocationQueryRaw = {
      [PlaceTokens.PRM_ID]: targetId
    }
    return {
      name: RouteNames.LAUNCH_REPORT_ROUTE,
      query: query
    };
  }

  private static createWelcomeRoute(item: UIMenuItem): RouteLocationRaw | undefined {
    let targetRecord: EntityReferenceData | undefined = item.target?.targetRecord;
    if (!targetRecord)
      return;
    let query: LocationQueryRaw = {
      [PlaceTokens.PRM_DASHBOARDID]: targetRecord.id,
      [PlaceTokens.PRM_ENTITY_TYPE]: BSCEntities.DashBoardGroup,
      [PlaceTokens.PRM_MENU_HASH]: (item.code || item.id || ""),
    };
    return {
      name: RouteNames.WELCOME_ROUTE,
      query: query
    };
  }

  private static createListRoute(item: UIMenuItem): RouteLocationRaw {
    let target = item.target!;
    return {
      name: RouteNames.LIST_VIEW_ROUTE,
      query: {
        [PlaceTokens.PRM_MENU_HASH]: item.code,
        [PlaceTokens.PRM_ENTITY_TYPE]: target.targetEntity,
        [PlaceTokens.PRM_LIST_VIEW_NAME]: target.viewName
      }
    };
  }

  static itemPreventedBySecurityProfile(group: UIMenuSubGroup, item: namaTypes.DTOMenuItem) {
    if (!item)
      return false;
    let currentUser = LoginUtils.loginInfo?.userInfo;
    if (currentUser == null)
      return false;
    if (!currentUser.fullProfile)
      return false;
    if (ObjectChecker.isEmptyOrNull(currentUser.fullProfile.allowDisallowInMenus))
      return false;
    if (ObjectChecker.isNotEmptyOrNull(item.code) && this.menuItemDisallowedByCode(currentUser, item))
      return true;
    if (ObjectChecker.isNotEmptyOrNull(item.code) && this.menuItemAllowedByCode(currentUser, item))
      return false;
    if (!item.target)
      return false;
    if (ObjectChecker.isEmptyOrNull(item.target.targetEntity))
      return false;
    let allowed = this.calcAllowedOrDisallowedLinesFor(item.target.targetEntity, "Allow", currentUser);
    let disallowed = this.calcAllowedOrDisallowedLinesFor(item.target.targetEntity, "Prevent", currentUser);
    let groupCode = group.subGroupData.code;
    let parentCode = group.subGroupData.parentCode;
    if (this.anyLineForGroup(disallowed, groupCode))
      return true;
    if (this.anyLineForGroup(allowed, groupCode))
      return false;
    if (this.anyLineForGroup(disallowed, parentCode))
      return true;
    if (this.anyLineForGroup(allowed, parentCode))
      return false;
    return ObjectChecker.isNotEmptyOrNull(allowed);
  }

  private static anyLineForGroup(lines: namaTypes.DTOAllowItemInMenusLine[], groupCode: string | undefined): boolean {
    for (let line of lines)
      if (ObjectChecker.areEqual(groupCode, line.groupCode))
        return true;
    return false;
  }

  static menuItemAllowedByCode(currentUser: namaTypes.DTOUser, item: namaTypes.DTOMenuItem): boolean {
    return this.hasCodeLine(currentUser, item.code, "Allow");
  }

  static menuItemDisallowedByCode(currentUser: namaTypes.DTOUser, item: namaTypes.DTOMenuItem): boolean {
    return this.hasCodeLine(currentUser, item.code, "Prevent");
  }

  private static hasCodeLine(currentUser: namaTypes.DTOUser, code: string | undefined, allowOrDisallow: string): boolean {
    let lines = currentUser.fullProfile?.allowDisallowInMenus;
    if (!lines)
      return false;
    for (let line of lines)
      if (line.elementCode === code && line.allowDisallow === allowOrDisallow)
        return true;
    return false;
  }

  static calcAllowedOrDisallowedLinesFor(entity: string | undefined, allowOrDisallow: string, currentUser: namaTypes.DTOUser): namaTypes.DTOAllowItemInMenusLine[] {
    let result: namaTypes.DTOAllowItemInMenusLine[] = [];
    let lines = currentUser.fullProfile?.allowDisallowInMenus;
    if (!lines)
      return result;
    for (let line of lines)
      if (this.ifTypeApplied(entity, line, allowOrDisallow))
        result.push(line);
    return result;
  }

  static ifTypeApplied(entity: string | undefined, l: namaTypes.DTOAllowItemInMenusLine, allowOrDisallow: string) {
    if (ObjectChecker.isNotEmptyOrNull(l.elementCode))
      return false;
    if (ObjectChecker.areNotEqual(allowOrDisallow, l.allowDisallow))
      return false;
    if (ObjectChecker.areEqual(l.entityType, entity))
      return true;
    if (!l.entityTypeList || !l.entityTypeList[CommonFieldIds.EXTRA_DATA])
      return false;
    return (l.entityTypeList[CommonFieldIds.EXTRA_DATA]![DomainBaseFieldIDs.EntityTypeList_lines_entityType] || "").includes(entity + ",");
  }

  static translateMenuItem(childItem: namaTypes.DTOMenuItem, otherLang: boolean = false) {
    if (otherLang)
      Translator.switchLang();
    let title = Translator.translateMenuItem(childItem);
    if (title && title.length) {
      Translator.restoreLang();
      return title;
    }
    if (!childItem.target) {
      Translator.restoreLang();
      return childItem.titleId;
    }
    switch (childItem.target.type) {
      case MenuTargetType.AllReports:
        break;
      case MenuTargetType.EntityList:
        title = Translator.getListViewTitle(childItem.target!.targetEntity!, childItem.target.viewName, null, childItem);
        break;
      case MenuTargetType.NewRecord:
        title = Translator.translateId(childItem.target.targetEntity);
        break;
      case MenuTargetType.Report:
      case MenuTargetType.Dashboard:
      case MenuTargetType.ReportsGroup:
      case MenuTargetType.Record:
        title = Translator.translateRef(childItem.target.targetRecord);
        break;
      case MenuTargetType.InternalLink:
      case MenuTargetType.Link:
        title = childItem.target.link;
        break;
    }
    Translator.restoreLang();
    return title;
  }

  static calcMenuItemSearchStr(childItem: namaTypes.DTOMenuItem): string {
    let title = this.translateMenuItem(childItem, !Translator.isArabic());
    let title2 = this.translateMenuItem(childItem, Translator.isArabic());
    if (!childItem.target) {
      return title + " " + title2 + (childItem['title'] && childItem['title'].id ? childItem['title'].id : "");
    }
    let entity = childItem.target.targetEntity;
    switch (childItem.target.type) {
      case MenuTargetType.AllReports:
        break;
      case MenuTargetType.EntityList:
        title += " " + entity?.toLowerCase();
        title += " " + Translator.translateIdAr(entity)!.toLowerCase();
        title += " " + StringUtils.normalizeArabic(Translator.translateIdAr(entity)!.toLowerCase());
        title += " " + Translator.translateIdAr(entity + "s")!.toLowerCase();
        title += " " + StringUtils.normalizeArabic(Translator.translateIdAr(entity + "s")!.toLowerCase());
        title2 += " " + Translator.translateIdEn(entity)!.toLowerCase();
        title2 += " " + Translator.translateIdEn(entity + "s")!.toLowerCase();
        break;
      case MenuTargetType.NewRecord:
        title += " " + entity + "s";
        title += " " + Translator.translateIdAr(entity + "s");
        title2 += " " + Translator.translateIdEn(entity + "s");
        break;
      case MenuTargetType.Report:
      case MenuTargetType.Dashboard:
      case MenuTargetType.ReportsGroup:
      case MenuTargetType.Record:
        title += " " + childItem.target?.targetRecord ? childItem.target.targetRecord?.code ?? "" : "";
        title2 = " " + childItem.target?.targetRecord ? ObjectChecker.getFirstNotEmptyObj(childItem.target.targetRecord?.code, childItem.target.targetRecord?.altCode) : "";
        break;
    }
    return title + " " + title2;
  }

  static shouldOpenInNewTab(e:  Event | MouseEvent | undefined) :boolean{
    return !!(e && (e.altKey || e.ctrlKey));
  }
}
